import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { UpgradeSubscriptionModal, EditPlanModal } from '@src/containers/SubscriptionModals';
import { Button } from '@src/components/Button';
import { PricingAndNextPayment } from '@src/views/Settings/SettingsBilling/PricingAndNextPayment';
import { ConfirmationModal } from '@src/components/ConfirmationModal';
import { setErrorFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import { fetchNotice } from '@src/redux/actions/notice';
import { setModalContent } from '@src/redux/actions/modal';
import { SUBSCRIBE_ERROR_MESSAGES } from '@src/constants/subscription';
import { PRICING_PAGE } from '@src/constants';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { trackEvent } from '@src/metrics';
import { sendNewIntercomMessage } from '@src/intercom';
import { useActivateSubscriptionForPlansMutation } from '@src/graphql/generated';
import './styles.scss';

const _SubscribeButton = ({
  customerId,
  currentPlanType,
  nextPlanType,
  trialEndsAt,
  setErrorFlashMessage,
  setSuccessFlashMessage,
  fetchNotice,
  subscriptionCycle,
  setModalContent,
  children,
}) => {
  const [activateSubscription, { loading: activateSubscriptionLoading }] = useActivateSubscriptionForPlansMutation({
    onCompleted: () => {
      fetchNotice(); // fetch notice again to hide top bar
      setSuccessFlashMessage('Subscription has successfully activated');
    },
    onError: ({ graphQLErrors }) => {
      graphQLErrors.forEach(graphQLError => {
        const code = get(graphQLError, 'extensions.exception.code');

        if (code) {
          setErrorFlashMessage(SUBSCRIBE_ERROR_MESSAGES[code]);
        }
      });
    },
  });

  return (
    <Button
      isDisabled={activateSubscriptionLoading}
      onClick={() => {
        setModalContent({
          content: (
            <ConfirmationModal
              body={<PricingAndNextPayment selectedPlanType={nextPlanType} />}
              footer={
                <p className="subscription-plans-button__subscribe-confirmation-modal-footer">
                  Billing is done at the beginning of the renewal cycle.
                </p>
              }
              onConfirm={() => {
                activateSubscription({
                  variables: {
                    planType: nextPlanType,
                    subscriptionCycle: subscriptionCycle,
                  },
                });

                trackEvent({
                  context: MetricContext.BILLING,
                  interaction: MetricAction.ACTIVATE_SUBSCRIPTION,
                  payload: {
                    'Customer': customerId,
                    'New Subscription Plan': nextPlanType,
                    'Old Subscription Plan': currentPlanType,
                    'Trial Ends At': trialEndsAt,
                  },
                });
              }}
              subTitle="Subscribe and get access to all your applicants."
            />
          ),
          title: 'Confirm subscription',
        });
      }}
    >
      {children}
    </Button>
  );
};

_SubscribeButton.propTypes = {
  children: PropTypes.node.isRequired,
  currentPlanType: PropTypes.string.isRequired,
  customerId: PropTypes.string.isRequired,
  fetchNotice: PropTypes.func.isRequired,
  nextPlanType: PropTypes.string.isRequired,
  setErrorFlashMessage: PropTypes.func.isRequired,
  setModalContent: PropTypes.func.isRequired,
  setSuccessFlashMessage: PropTypes.func.isRequired,
  subscriptionCycle: PropTypes.string.isRequired,
  trialEndsAt: PropTypes.string.isRequired,
};

const _UpgradeButton = ({ children, setModalContent }) => (
  <Button
    onClick={() =>
      setModalContent({
        content: <UpgradeSubscriptionModal />,
      })
    }
  >
    {children}
  </Button>
);

_UpgradeButton.propTypes = {
  children: PropTypes.node.isRequired,
  setModalContent: PropTypes.func.isRequired,
};

export const CancelPlanButton = ({ planType, customerId, trialEndsAt }) => (
  <div
    className="subscription-plans-button subscription-plans-button--margin subscription-plans-button--grey"
    onClick={() => {
      sendNewIntercomMessage("Hi! I'd like to cancel our subscription.");

      trackEvent({
        context: MetricContext.BILLING,
        interaction: MetricAction.CANCEL_SUBSCRIPTION,
        payload: {
          'Customer': customerId,
          'Old Subscription Plan': planType,
          'Trial Ends At': trialEndsAt,
        },
      });
    }}
  >
    Cancel plan
  </div>
);

CancelPlanButton.propTypes = {
  customerId: PropTypes.string.isRequired,
  planType: PropTypes.string.isRequired,
  trialEndsAt: PropTypes.string.isRequired,
};

export const DowngradePlanButton = ({ planType, customerId, trialEndsAt }) => (
  <div
    className="subscription-plans-button subscription-plans-button--blue"
    onClick={() => {
      sendNewIntercomMessage("Hi! I'd like to downgrade our subscription plan.");

      trackEvent({
        context: MetricContext.BILLING,
        interaction: MetricAction.DOWNGRADE_SUBSCRIPTION,
        payload: {
          'Customer': customerId,
          'Old Subscription Plan': planType,
          'Trial Ends At': trialEndsAt,
        },
      });
    }}
  >
    Downgrade
  </div>
);

DowngradePlanButton.propTypes = {
  customerId: PropTypes.string.isRequired,
  planType: PropTypes.string.isRequired,
  trialEndsAt: PropTypes.string.isRequired,
};

export const SeePlansButton = () => (
  <a
    className="subscription-plans-button subscription-plans-button--margin subscription-plans-button--blue"
    href={PRICING_PAGE}
    target="_blank"
    rel="noreferrer"
  >
    See plans
  </a>
);

const _EditPlanButton = ({ setModalContent }) => {
  return (
    <div
      className="subscription-plans-button subscription-plans-button--blue"
      onClick={() => {
        setModalContent({
          content: <EditPlanModal />,
          title: 'Edit Plan',
        });
      }}
    >
      Edit Plan
    </div>
  );
};

_EditPlanButton.propTypes = {
  setModalContent: PropTypes.func.isRequired,
};

export const EditPlanButton = connect(null, { setModalContent })(_EditPlanButton);
export const SubscribeButton = connect(null, {
  fetchNotice,
  setErrorFlashMessage,
  setModalContent,
  setSuccessFlashMessage,
})(_SubscribeButton);
export const UpgradeButton = connect(null, { setModalContent })(_UpgradeButton);
